import {defineStore} from 'pinia'
import {computed, ref} from 'vue'
import {useSocketStore} from '@toolify/client/src/stores/SocketStore/useSocketStore'
import {useCallInviteApiStore} from '@toolify/client/src/stores/api/CallInviteApiStore/useCallInviteApiStore'
import {GlobalSocketEventType} from '@toolify/server/src/services/SocketService/enum/GlobalSocketEventType'
import {
  ICallInviteCreatedEmitPayload,
} from '@toolify/server/src/services/SocketService/strategies/GlobalSocketEventTypeStrategy/ICallInviteCreatedEmitPayload'
import {
  ICallInviteUpdatedEmitPayload,
} from '@toolify/server/src/services/SocketService/strategies/GlobalSocketEventTypeStrategy/ICallInviteUpdatedEmitPayload'
import {
  ICallInviteResponseInstance,
} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/ICallInviteResponseInstance'
import {CallInviteStatusType} from '@toolify/server/src/models/mongoose/CallInviteModel/enum/CallInviteStatusType'
import {useAuthStore} from '@toolify/client/src/stores/AuthStore/useAuthStore'
import {watch} from 'vue'
import {EntityType} from '@toolify/server/src/services/EntityService/enum/EntityType'

export const useCallInviteStore = defineStore('callInvite', () => {
  const socketStore = useSocketStore()
  const callInviteApiStore = useCallInviteApiStore()
  const authStore = useAuthStore()

  const ringingInvites = ref<ICallInviteResponseInstance[]>([])
  let stopAuthWatch: (() => void) | null = null
  const expiryTimeoutByInviteId = new Map<string, ReturnType<typeof setTimeout>>()

  const activeRingingInvite = computed<ICallInviteResponseInstance>(() => {
    const now = Date.now()
    return ringingInvites.value.find(invite => {
      return invite.status === CallInviteStatusType.Ringing
        && new Date(invite.expiresAt).getTime() > now
    }) || null
  })

  function clearExpiryTimeout(inviteId: string) {
    const timeout = expiryTimeoutByInviteId.get(inviteId)
    if (!timeout) {
      return
    }
    clearTimeout(timeout)
    expiryTimeoutByInviteId.delete(inviteId)
  }

  function clearAllExpiryTimeouts() {
    for (const inviteId of Array.from(expiryTimeoutByInviteId.keys())) {
      clearExpiryTimeout(inviteId)
    }
  }

  function removeInvite(inviteId: string) {
    clearExpiryTimeout(inviteId)
    if (!ringingInvites.value.some(invite => invite.id === inviteId)) {
      return
    }
    ringingInvites.value = ringingInvites.value.filter(invite => invite.id !== inviteId)
  }

  function scheduleExpiry(invite: ICallInviteResponseInstance) {
    clearExpiryTimeout(invite.id)
    const remainingMs = new Date(invite.expiresAt).getTime() - Date.now()
    if (remainingMs <= 0) {
      removeInvite(invite.id)
      return
    }
    expiryTimeoutByInviteId.set(invite.id, setTimeout(() => {
      removeInvite(invite.id)
    }, remainingMs))
  }

  function upsertInvite(invite: ICallInviteResponseInstance) {
    const now = Date.now()
    const expiresAtTime = new Date(invite.expiresAt).getTime()
    const isActive = invite.status === CallInviteStatusType.Ringing
      && expiresAtTime > now

    const existingIdx = ringingInvites.value.findIndex(i => i.id === invite.id)
    if (!isActive) {
      removeInvite(invite.id)
      return
    }
    scheduleExpiry(invite)
    if (existingIdx >= 0) {
      ringingInvites.value = ringingInvites.value.map(i => i.id === invite.id ? invite : i)
      return
    }
    ringingInvites.value = [invite, ...ringingInvites.value]
  }

  function onInviteCreated(payload: ICallInviteCreatedEmitPayload) {
    upsertInvite(payload.callInvite)
  }

  function onInviteUpdated(payload: ICallInviteUpdatedEmitPayload) {
    upsertInvite(payload.callInvite)
  }

  async function refreshRingingInvites() {
    clearAllExpiryTimeouts()
    if (authStore.currentEntity?.entityType !== EntityType.User || !authStore.currentEntity.entityId) {
      ringingInvites.value = []
      return
    }
    const invites = await callInviteApiStore.getRingingCallInvites()
    const now = Date.now()
    const activeInvites = invites.filter(invite => {
      return invite.status === CallInviteStatusType.Ringing
        && new Date(invite.expiresAt).getTime() > now
    })
    ringingInvites.value = activeInvites
    activeInvites.forEach(scheduleExpiry)
  }

  function initialize() {
    socketStore.socket.on(GlobalSocketEventType.CallInviteCreated, onInviteCreated)
    socketStore.socket.on(GlobalSocketEventType.CallInviteUpdated, onInviteUpdated)
    stopAuthWatch = watch(() => authStore.currentEntity?.entityId, async (value, oldValue) => {
      if (value === oldValue) {
        return
      }
      await refreshRingingInvites()
    }, {immediate: true})
  }

  function dispose() {
    socketStore.socket.off(GlobalSocketEventType.CallInviteCreated, onInviteCreated)
    socketStore.socket.off(GlobalSocketEventType.CallInviteUpdated, onInviteUpdated)
    stopAuthWatch?.()
    stopAuthWatch = null
    clearAllExpiryTimeouts()
    ringingInvites.value = []
  }

  return {
    ringingInvites,
    activeRingingInvite,
    refreshRingingInvites,
    initialize,
    dispose,
  }
})

